import React from "react";
import {
  useFetchCustomerOrderDetails,
  useOrderModificationDecision,
  useOrderModificationSummary,
} from "../../hooks/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  MODIFICATION_ORDERS_COLUMN,
  ORDER_MODIFICATION_DECISION,
  ORDER_STATUSES,
} from "../../constants/constants";
import { PAGE_RELOAD_DELAY_MS } from "../../constants/config";
import CustomTable from "../../widgets/Table";
import Loader from "../../components/Loader/Loader";
import { useSnackbarContext } from "../../widgets/CustomSnackBar/CustomSnackBar";
import SecondaryButton from "../../widgets/Button/SecondaryButton/SecondaryButton";

import "./OrderModification.css";

const handleOrderModificationDecision = (openSnackbar, proposalId, orderId, orderModificationDecision, operationType) => {
    const payload = {
      order_id: orderId,
      proposal_id: proposalId,
      operation: operationType,
    };
  orderModificationDecision(payload, {
    onSuccess: () => {
      openSnackbar("Order modification decision", "success");
      window.setTimeout(() => window.location.reload(), PAGE_RELOAD_DELAY_MS);
    },
    onError: (error) => {
      openSnackbar(error.message || "Failed to update order modification decision", "error");
      window.setTimeout(() => window.location.reload(), PAGE_RELOAD_DELAY_MS);
    },
  });
};
const getModificationDetails = (
  modificationRequestOrders,
  openSnackbar,
  proposalId,
  orderId,
  orderModificationDecision,
  isOrderModificationDecisionLoading
) => {
  if (!Array.isArray(modificationRequestOrders)) {
    return [];
  }

  return modificationRequestOrders.map((orderDetails) => ({
    orderId: orderDetails?.order_id ?? "N/A",
    amount: orderDetails?.inr_conversion_amount
      ? Number(orderDetails.inr_conversion_amount).toLocaleString("en-IN")
      : "—",
    orderStatus: orderDetails?.status?.name ?? "Unknown",
    actions: (
      <div className="order-modification-actions">
        {isOrderModificationDecisionLoading ? <Loader /> : (
        <>
        <SecondaryButton
          onClick={() =>
            handleOrderModificationDecision(
              openSnackbar,
              proposalId,
              orderId,
              orderModificationDecision,
              ORDER_MODIFICATION_DECISION.ACCEPT,
            )
          }
        >
          Accept
        </SecondaryButton>
        <SecondaryButton
          onClick={() =>
            handleOrderModificationDecision(
              openSnackbar,
              proposalId,
              orderId,
              orderModificationDecision,
              ORDER_MODIFICATION_DECISION.REJECT
            )
          }
        >
          Reject
        </SecondaryButton>
        </>
        )}
      </div>
    ),
  }));
};

const OrderModification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { openSnackbar } = useSnackbarContext();
  const uamId = searchParams.get("uamId");

  const {
    isPending: isOrderDetailsLoading,
    data: orderDetails,
    error: orderDetailsError,
    isError: isErrorFetchingOrderDetails,
  } = useFetchCustomerOrderDetails(uamId);

  const {
    mutate: orderModificationDecision,
    isLoading: isOrderModificationDecisionLoading,
  } = useOrderModificationDecision();

  const modificationRequestOrders =
    orderDetails?.filter(
      (order) => order.status.id === ORDER_STATUSES.SUSPENDED
    ) || [];

  const orderId = modificationRequestOrders[0]?.order_id;
  const hasModificationRequest = modificationRequestOrders.length > 0;
  const {
    data: orderModificationSummary,
    isPending: isOrderModificationSummaryLoading,
    error: orderModificationSummaryError,
  } = useOrderModificationSummary(hasModificationRequest ? orderId : null);

  const proposalId = orderModificationSummary?.proposal_order.id;

  const rows = getModificationDetails(
    modificationRequestOrders,
    openSnackbar,
    proposalId,
    orderId,
    orderModificationDecision,
    isOrderModificationDecisionLoading
  );
  if (isOrderDetailsLoading || isOrderModificationSummaryLoading) {
    return (
      <div className="order-modification-page">
        <div className="order-modification-card">
          <button
            type="button"
            className="order-modification-back"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowBackIcon sx={{ fontSize: 32 }} aria-hidden />
            back
          </button>
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="order-modification-page">
      <div className="order-modification-card">
        <button
          type="button"
          className="order-modification-back"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <ArrowBackIcon sx={{ fontSize: 32 }} aria-hidden />
          back
        </button>
        <header className="order-modification-header">
          <h1 className="order-modification-title">Order Modification</h1>
        </header>
        {modificationRequestOrders.length === 0 ? (
          <p className="order-modification-empty">No modification requests</p>
        ) : (
          <div className="order-modification-table-wrap">
            <CustomTable rows={rows} columns={MODIFICATION_ORDERS_COLUMN} />
          </div>
        )}
        {isErrorFetchingOrderDetails && (
          <div className="order-modification-error" role="alert">
            {orderDetailsError.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderModification;
